import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setHapticsEnabled } from '@/store/settings.slice';
import { setTourActive } from '@/store/core.slice';
import { LINGUE, nomeNativo, salvaLingua } from '@/lib/lingua';

/**
 * Preferences panel — language, haptics, replay tutorial. Reached from the
 * Profilo hub's "— Impostazioni —" block. (Split out of the old Settings.tsx.)
 */
export function Preferences() {
  const { t, i18n } = useTranslation('general');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const settings = useAppSelector((s) => s.settings);

  // La lingua attiva la dice i18n, non lo store: prima l'etichetta «Attivo» leggeva uno
  // stato che partiva da 'en' e non seguiva la lingua rilevata.
  const attiva = i18n.resolvedLanguage ?? i18n.language;

  const handleLanguage = (code: string) => {
    salvaLingua(code);
    void i18n.changeLanguage(code);
  };

  return (
    <div className="flex flex-col gap-6 px-4 pb-6 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
      <header className="sr-only">
        <h1>
          {t('preferences', { defaultValue: 'Preferences' })}
        </h1>
      </header>

      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-fg-muted">
          {t('language', { defaultValue: 'Language' })}
        </h2>
        <div className="card-edge flex flex-col gap-1 overflow-hidden rounded-xl bg-bg-secondary">
          {LINGUE.map((code) => (
            <button
              key={code}
              type="button"
              lang={code}
              onClick={() => handleLanguage(code)}
              className="flex items-center justify-between px-3 py-3 text-left transition-opacity active:opacity-80"
            >
              <span className="text-sm text-fg">{nomeNativo(code)}</span>
              {attiva === code && (
                <span className="text-xs font-medium text-brand">
                  {t('active', { defaultValue: 'Active' })}
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-2">
        <label className="card-edge flex items-center justify-between rounded-xl bg-bg-secondary p-3">
          <span className="text-sm text-fg">
            {t('haptics', { defaultValue: 'Haptic feedback' })}
          </span>
          <input
            type="checkbox"
            className="accent-brand"
            checked={settings.hapticsEnabled}
            onChange={(e) => dispatch(setHapticsEnabled(e.target.checked))}
          />
        </label>
        <button
          type="button"
          onClick={() => {
            dispatch(setTourActive(true));
            navigate('/');
          }}
          className="card-edge rounded-xl bg-bg-secondary p-3 text-left text-sm font-medium text-fg transition-opacity active:opacity-80"
        >
          {t('settings.replayTutorial', { ns: 'onboarding', defaultValue: 'Replay the tutorial' })}
        </button>
      </section>
    </div>
  );
}
